import { useEffect, useState, useCallback } from 'react';
import './Monitoring.css';
import { MonitoringRunDetail } from './MonitoringRunDetail';

type Kind = 'gmail' | 'health';

interface MonitoringRun {
  id: string;
  kind: Kind;
  status: 'running' | 'done' | 'failed';
  triggered_by: string;
  started_at: string;
  finished_at: string | null;
  total_checked: number;
  new_flagged: number;
  still_flagged: number;
  cleared: number;
  errors: number;
  error_message: string | null;
}

interface Finding {
  id: string;
  kind: Kind;
  email: string;
  status: string;
  sender: string | null;
  snippet: string | null;
  first_detected_at: string;
  last_seen_at: string;
  cleared_at: string | null;
  dismissed_by: string | null;
}

interface Summary {
  kind: Kind;
  lastRunAt: string | null;
  nextRunAt: string | null;
  intervalMs: number;
  healthDot: 'green' | 'yellow' | 'red';
  healthReason: string;
  activeFindings: number;
  inFlight: boolean;
}

interface Props {
  kind: Kind;
}

const TITLES: Record<Kind, string> = {
  gmail: 'Gmail Regular Monitoring',
  health: 'Admin Panel Health',
};

function formatAgo(iso: string | null): string {
  if (!iso) return '-';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  return `${(m / 60).toFixed(1)}h ago`;
}

function formatIn(iso: string | null): string {
  if (!iso) return '-';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms < 0) return 'overdue';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `in ${m} min`;
  return `in ${(m / 60).toFixed(1)}h`;
}

export function Monitoring({ kind }: Props): JSX.Element {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [runs, setRuns] = useState<MonitoringRun[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [sumRes, runsRes, findRes] = await Promise.all([
        fetch('/api/monitoring/summary').then((r) => r.json()),
        fetch(`/api/monitoring/runs?kind=${kind}&limit=50`).then((r) => r.json()),
        fetch(`/api/monitoring/findings?kind=${kind}&active=true`).then((r) => r.json()),
      ]);
      if (!sumRes.success) throw new Error(sumRes.error?.message ?? 'summary failed');
      if (!runsRes.success) throw new Error(runsRes.error?.message ?? 'runs failed');
      if (!findRes.success) throw new Error(findRes.error?.message ?? 'findings failed');
      setSummary(sumRes.data[kind]);
      setRuns(runsRes.data);
      setFindings(findRes.data);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [kind]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(id);
  }, [refresh]);

  async function triggerRun() {
    setRunning(true);
    setError(null);
    try {
      const r = await fetch('/api/monitoring/runs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind }),
      }).then((res) => res.json());
      if (!r.success) throw new Error(r.error?.message ?? 'trigger failed');
      for (let i = 0; i < 200; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        await refresh();
        const latest = await fetch(`/api/monitoring/runs?kind=${kind}&limit=1`).then((res) => res.json());
        if (latest.success && latest.data[0]?.status !== 'running') break;
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  async function dismissFinding(id: string) {
    setError(null);
    try {
      const r = await fetch(`/api/monitoring/findings/${id}/dismiss`, { method: 'POST' }).then((res) => res.json());
      if (!r.success) throw new Error(r.error?.message ?? 'dismiss failed');
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="monitoring-page">
      <header className="monitoring-header">
        <h1>{TITLES[kind]}</h1>
        {summary && (
          <div className={`monitoring-status monitoring-status-${summary.healthDot}`}>
            <span className="monitoring-dot" />
            <span className="monitoring-status-label">
              {summary.inFlight ? 'Running now' :
               summary.healthDot === 'green' ? 'Active' :
               summary.healthDot === 'yellow' ? 'Delayed' : 'Stalled'}
            </span>
            <span className="monitoring-status-meta">
              Last run {formatAgo(summary.lastRunAt)}
              {summary.nextRunAt && ` ${'•'} Next ${formatIn(summary.nextRunAt)}`}
            </span>
          </div>
        )}
      </header>

      {error && <div className="monitoring-error">{error}</div>}

      <section className="monitoring-section">
        <div className="monitoring-section-header">
          <h2>Active findings ({findings.length})</h2>
          <button className="monitoring-run-btn" disabled={running} onClick={() => void triggerRun()}>
            {running ? 'Running...' : 'Run now'}
          </button>
        </div>
        {findings.length === 0 ? (
          <p className="monitoring-empty">No active findings.</p>
        ) : (
          <table className="monitoring-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Status</th>
                <th>First detected</th>
                <th>Last seen</th>
                {kind === 'gmail' && <th>From</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {findings.map((f) => (
                <tr key={f.id}>
                  <td>{f.email}</td>
                  <td>{f.status}</td>
                  <td>{new Date(f.first_detected_at).toLocaleString()}</td>
                  <td>{formatAgo(f.last_seen_at)}</td>
                  {kind === 'gmail' && <td title={f.snippet ?? ''}>{f.sender ?? '-'}</td>}
                  <td>
                    <button className="monitoring-dismiss-btn" onClick={() => void dismissFinding(f.id)}>
                      Dismiss
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="monitoring-section">
        <h2>Run history</h2>
        {runs.length === 0 ? (
          <p className="monitoring-empty">No runs yet.</p>
        ) : (
          <table className="monitoring-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Status</th>
                <th>Triggered by</th>
                <th>Checked</th>
                <th>New</th>
                <th>Still</th>
                {kind === 'health' && <th>Cleared</th>}
                <th>Errors</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr
                  key={r.id}
                  className={`monitoring-run-row monitoring-run-${r.status}`}
                  onClick={() => setOpenRunId(r.id)}
                  title="Click to see run details"
                >
                  <td>{new Date(r.started_at).toLocaleString()}</td>
                  <td>{r.status}</td>
                  <td>{r.triggered_by}</td>
                  <td>{r.total_checked}</td>
                  <td>{r.new_flagged}</td>
                  <td>{r.still_flagged}</td>
                  {kind === 'health' && <td>{r.cleared}</td>}
                  <td>{r.errors}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {openRunId && (
        <MonitoringRunDetail
          runId={openRunId}
          kind={kind}
          onClose={() => setOpenRunId(null)}
        />
      )}
    </div>
  );
}
